import type { Order } from "@/lib/projects";

type TimesheetModalModule = {
  openTimesheetFlowForProjectPreset: (preset: Record<string, unknown>) => void;
  openTimesheetForEdit: (timesheet: Record<string, unknown>) => void;
};

let bootstrapPromise: Promise<void> | null = null;
let modalsPromise: Promise<void> | null = null;
let modulePromise: Promise<TimesheetModalModule> | null = null;

/** Lädt ein ES-Modul von absoluter URL zur Laufzeit (nicht durch Vite gebündelt). */
function importAbsolute<T>(url: string): Promise<T> {
  const fn = new Function("url", "return import(url)") as (u: string) => Promise<T>;
  return fn(url);
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.type = "module";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Script load failed: ${src}`));
    document.head.appendChild(s);
  });
}

async function ensureBootstrap(): Promise<void> {
  if (window.bootstrap?.Modal) return;
  if (!bootstrapPromise) {
    bootstrapPromise = loadScript("/static/vendor/bootstrap/bootstrap.bundle.min.js").then(() => {
      window.dispatchEvent(new Event("eh:bootstrap-ready"));
    });
  }
  await bootstrapPromise;
}

async function ensureTimesheetModalsDom(): Promise<void> {
  if (document.getElementById("modalTimesheet")) return;

  if (!modalsPromise) {
    modalsPromise = (async () => {
      let host = document.querySelector("eh-timesheet-modals");
      if (!host) {
        host = document.createElement("eh-timesheet-modals");
        document.body.appendChild(host);
      }
      await loadScript("/static/partials/timesheet-modals.js");
      await new Promise<void>((resolve) => {
        const onReady = () => {
          document.removeEventListener("eh:timesheet-modals-ready", onReady);
          resolve();
        };
        document.addEventListener("eh:timesheet-modals-ready", onReady);
        if (document.getElementById("modalTimesheet")) {
          document.removeEventListener("eh:timesheet-modals-ready", onReady);
          resolve();
        }
        setTimeout(resolve, 8000);
      });
    })();
  }
  await modalsPromise;
}

async function getTimesheetModule(): Promise<TimesheetModalModule> {
  if (!modulePromise) {
    modulePromise = (async () => {
      await ensureBootstrap();
      await ensureTimesheetModalsDom();
      if (!document.querySelector('script[src="/static/config.js"]')) {
        await loadScript("/static/config.js");
      }
      return importAbsolute<TimesheetModalModule>("/static/timesheets/modal.js");
    })();
  }
  return modulePromise;
}

export async function openTimesheetCreateForProject(project: Order): Promise<void> {
  const mod = await getTimesheetModule();
  const preset = {
    auftrag_id: project.id,
    auftragsnummer: project.auftragsnummer,
    jahr: project.jahr,
    kunde: project.kunde,
    ort: project.ort,
    hero_project_id: project.hero_project_id,
  };
  mod.openTimesheetFlowForProjectPreset(preset);
}

export async function openTimesheetEdit(timesheet: Record<string, unknown>): Promise<void> {
  const mod = await getTimesheetModule();
  mod.openTimesheetForEdit(timesheet);
}

type TimesheetListRowModule = {
  openTimesheetFromListRow: (ts: Record<string, unknown>) => Promise<void>;
};

let listRowModulePromise: Promise<TimesheetListRowModule> | null = null;

async function getTimesheetListRowModule(): Promise<TimesheetListRowModule> {
  if (!listRowModulePromise) {
    listRowModulePromise = (async () => {
      await ensureBootstrap();
      await ensureTimesheetModalsDom();
      if (!document.querySelector('script[src="/static/config.js"]')) {
        await loadScript("/static/config.js");
      }
      return importAbsolute<TimesheetListRowModule>("/stundennachweise.js");
    })();
  }
  return listRowModulePromise;
}

/** Öffnet den klassischen Status-/Aktions-Flow für einen Listen-Eintrag. */
export async function openTimesheetFromListRow(ts: Record<string, unknown>): Promise<void> {
  const mod = await getTimesheetListRowModule();
  await mod.openTimesheetFromListRow(ts);
}

const REFRESH_MODAL_IDS = ["modalTimesheet", "modalTimesheetFestpreis"];

export function wireTimesheetModalRefresh(onRefresh: () => void): () => void {
  const handlers: Array<{ el: Element; fn: () => void }> = [];

  function bind() {
    for (const id of REFRESH_MODAL_IDS) {
      const el = document.getElementById(id);
      if (!el || el.dataset.ehPoRefreshBound === "1") continue;
      el.dataset.ehPoRefreshBound = "1";
      const fn = () => onRefresh();
      el.addEventListener("hidden.bs.modal", fn);
      handlers.push({ el, fn });
    }
  }

  bind();
  const t = window.setInterval(bind, 500);
  window.setTimeout(() => window.clearInterval(t), 10000);

  return () => {
    window.clearInterval(t);
    for (const { el, fn } of handlers) {
      el.removeEventListener("hidden.bs.modal", fn);
    }
  };
}

declare global {
  interface Window {
    bootstrap?: { Modal: { getOrCreateInstance: (el: Element) => { show: () => void; hide: () => void } } };
  }
}
